import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { OrderDetail } from '@/lib/api-admin/types';
import { deriveOrderStatement, deriveTransactionStatement } from './derive';
import type {
  OrderStatement,
  PrintDocEnvelope,
  PrintDocumentFieldValue,
  PrintDocumentKind,
  TransactionStatement,
} from './types';

/**
 * 16차 TASK-8 — 주문 출력물(거래명세서/주문내역서) 조회.
 *
 * 견적서(quote)는 BO 범위 밖 — §3 "회원등급할인만(상품할인 미반영)" 은 cart(정가) 출처라
 * SF 장바구니에서 구현한다(order=salePrice 라 BO order 기반은 소스 오류). 여기선 제외.
 *
 * be-order 계약(2026-06-06): Order 에서 파생한 read-only print DTO(신규 영속 없음).
 *   GET /admin/v1/orders/{orderId}/transaction-statement
 *   GET /admin/v1/orders/{orderId}/order-statement
 * 게이트웨이 broad `/admin/v1/orders/**` → BE `/api/admin/v1/orders/**` 재사용(라우팅 변경 없음).
 * envelope `{ success, data, error, meta }` + Money{amount,currencyCode} + NON_NULL 직렬화.
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 adminFetch 로 직접 호출한다.
 * BE 미배포(404/네트워크 실패) 시, 이미 로드된 OrderDetail 에서 파생해 폴백한다
 * — claims processing-defaults 의 404→폴백 선례와 동일(BO 가 화면을 항상 그린다).
 * BE 응답이 우선이며 레이아웃 변경은 없다.
 */

/** 계약상 엔드포인트 경로 세그먼트(kind → path). */
const PATH_SEGMENT: Record<PrintDocumentKind, string> = {
  'transaction-statement': 'transaction-statement',
  'order-statement': 'order-statement',
};

export type PrintDocumentData =
  | { kind: 'transaction-statement'; doc: TransactionStatement }
  | { kind: 'order-statement'; doc: OrderStatement };

export const printDocumentQueryKey = (orderId: string, kind: PrintDocumentKind) =>
  ['admin', 'order-print-document', orderId, kind] as const;

/** OrderDetail 폴백으로 정규화 모델을 파생한다. */
const deriveFromOrder = (
  kind: PrintDocumentKind,
  order: OrderDetail,
): PrintDocumentData => {
  switch (kind) {
    case 'transaction-statement':
      return { kind, doc: deriveTransactionStatement(order) };
    case 'order-statement':
      return { kind, doc: deriveOrderStatement(order) };
  }
};

/** envelope.data 를 kind 에 맞는 정규화 모델로 매핑(계약 형태 동일). */
const mapEnvelope = (
  kind: PrintDocumentKind,
  data: unknown,
): PrintDocumentData => {
  switch (kind) {
    case 'transaction-statement':
      return { kind, doc: data as TransactionStatement };
    case 'order-statement':
      return { kind, doc: data as OrderStatement };
  }
};

/** order-form-field-values 응답 1행(BO 주문상세 OrderFormFieldValue 와 동일 형태). */
interface RawFieldValue {
  label?: string | null;
  value?: string | null;
  masked?: boolean | null;
}

interface FieldValuesEnvelope {
  success?: boolean;
  data?: { values?: RawFieldValue[] | null } | null;
}

/**
 * [233] 주문서 추가정보(통관 등) 폴백 조회 — be-order PrintDocument DTO 가 추가정보를
 * 동봉하지 않을 때(B안) BO 주문상세와 동일 엔드포인트로 합성한다. 404/에러/빈 값은
 * 빈 배열로 떨어뜨려 문서 렌더를 막지 않는다(부가정보이므로 fail-safe).
 */
const fetchOrderFormFieldValues = async (
  orderId: string,
): Promise<PrintDocumentFieldValue[]> => {
  const path = `/admin/v1/orders/${orderId}/order-form-field-values`;
  try {
    const res = await adminFetch(path);
    if (!res.ok) return [];
    const env = (await res.json()) as FieldValuesEnvelope;
    return (env.data?.values ?? [])
      .filter((v): v is RawFieldValue => v != null)
      .map((v) => ({
        label: v.label ?? '',
        value: v.value ?? '',
        masked: v.masked ?? false,
      }));
  } catch {
    return [];
  }
};

/** doc 에 추가정보가 없으면(BE 미동봉) 폴백 조회로 합성한다. 이미 있으면 그대로 둔다. */
const withFieldValues = async (
  orderId: string,
  result: PrintDocumentData,
): Promise<PrintDocumentData> => {
  const existing = result.doc.orderFormFieldValues;
  if (existing && existing.length > 0) return result;
  const values = await fetchOrderFormFieldValues(orderId);
  if (values.length === 0) return result;
  return {
    ...result,
    doc: { ...result.doc, orderFormFieldValues: values },
  } as PrintDocumentData;
};

export const fetchDocument = async (
  orderId: string,
  kind: PrintDocumentKind,
  fallbackOrder: OrderDetail,
): Promise<PrintDocumentData> => {
  const path = `/admin/v1/orders/${orderId}/${PATH_SEGMENT[kind]}`;
  try {
    const res = await adminFetch(path);
    if (!res.ok) {
      // 404(미배포) 등 → 폴백 파생. 그 외 상태도 출력물은 끊기지 않게 폴백.
      return withFieldValues(orderId, deriveFromOrder(kind, fallbackOrder));
    }
    const env = (await res.json()) as PrintDocEnvelope<unknown>;
    if (!env.success || env.data == null) {
      return withFieldValues(orderId, deriveFromOrder(kind, fallbackOrder));
    }
    return withFieldValues(orderId, mapEnvelope(kind, env.data));
  } catch {
    return withFieldValues(orderId, deriveFromOrder(kind, fallbackOrder));
  }
};

interface UsePrintDocumentOptions {
  orderId: string | undefined;
  kind: PrintDocumentKind;
  /** 폴백 파생을 위한, 이미 로드된 주문 상세. */
  order: OrderDetail | undefined;
  /** 모달이 열릴 때만 조회하도록 게이트. */
  enabled?: boolean;
}

export const usePrintDocument = ({
  orderId,
  kind,
  order,
  enabled = true,
}: UsePrintDocumentOptions) =>
  useQuery<PrintDocumentData>({
    queryKey: printDocumentQueryKey(orderId ?? '', kind),
    enabled: enabled && Boolean(orderId) && Boolean(order),
    queryFn: () => fetchDocument(orderId as string, kind, order as OrderDetail),
  });
